/**
 * CarouselDots — مؤشّر تقدّم موحَّد لكلّ كاروسيلات الموقع.
 *
 * القاعدة الموحَّدة (راجع `mem://design/carousel-progress`):
 *  - ≤ 7 عناصر → نقاط كلاسيكيّة (النشطة أعرض).
 *  - > 7 عناصر → عدّاد رقميّ + شريط تقدّم سلس يتحدّث مع كلّ سحب
 *    (يستمع `scroll` ـ ليس `select` فقط) فيرى المستخدم استجابة فوريّة.
 *
 * يستخدم API الـEmbla مباشرة فلا حاجة لتمرير index/onChange.
 * يعمل في RTL/LTR (Embla يعالج الاتجاه داخلياً).
 *
 * variant:
 *  - "default": للوضع على خلفيّة عاديّة (نقاط بـforeground/20، نشطة primary).
 *  - "overlay": للوضع فوق صورة/فيديو (نقاط بـbackground/70، كبسولة زجاجيّة).
 */

import * as React from "react";
import type { EmblaCarouselType } from "embla-carousel";
import { useLocale } from "@/contexts/LocaleContext";
import { cn } from "@/lib/utils";

export interface CarouselDotsProps {
  /** API الـEmbla — اختياريّ. لو غاب نعتمد على selectedIndex/onSelect المُمرَّرَين. */
  emblaApi?: EmblaCarouselType | undefined;
  count: number;
  ariaPrefix?: string;
  variant?: "default" | "overlay";
  /** كسر العتبة بين النقاط والـ counter+progress. الافتراضي ١ → progress دائماً. */
  threshold?: number;
  className?: string;
  /** الفهرس النشط — يُستخدم فقط في وضع non-Embla. */
  selectedIndex?: number;
  /** اختيار شريحة — يُستخدم فقط في وضع non-Embla. */
  onSelect?: (index: number) => void;
}

export function CarouselDots({
  emblaApi,
  count,
  ariaPrefix = "slide",
  variant = "default",
  threshold = 1,
  className,
  selectedIndex: selectedIndexProp,
  onSelect: onSelectProp,
}: CarouselDotsProps) {
  const { dir } = useLocale();
  const isRtl = dir === "rtl";
  const ofLabel = isRtl ? "من" : "of";

  const [emblaSelectedIndex, setEmblaSelectedIndex] = React.useState(0);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setEmblaSelectedIndex(emblaApi.selectedScrollSnap());
    const onScroll = () => {
      const p = Math.max(0, Math.min(1, emblaApi.scrollProgress()));
      setProgress(p);
    };
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("scroll", onScroll);
    onSelect();
    onScroll();
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("scroll", onScroll);
    };
  }, [emblaApi]);

  // وضع non-Embla: نشتقّ progress من selectedIndex/count.
  const selectedIndex = emblaApi ? emblaSelectedIndex : (selectedIndexProp ?? 0);
  const effectiveProgress = emblaApi
    ? progress
    : count > 1
      ? selectedIndex / (count - 1)
      : 0;

  const scrollTo = (i: number) => {
    if (emblaApi) emblaApi.scrollTo(i);
    else onSelectProp?.(i);
  };

  if (count <= 1) return null;

  const isOverlay = variant === "overlay";

  if (count <= threshold) {
    const inactiveCls = isOverlay
      ? "w-1.5 bg-background/60 hover:bg-background"
      : "w-1.5 bg-foreground/20 hover:bg-foreground/40";
    const activeCls = isOverlay ? "w-5 bg-background" : "w-5 bg-primary";

    const wrapperCls = isOverlay
      ? "flex items-center gap-1.5 rounded-full bg-foreground/25 px-2.5 py-1.5 backdrop-blur-md ring-1 ring-background/20"
      : "flex items-center justify-center gap-1.5";

    return (
      <div className={cn(wrapperCls, className)} dir="ltr">
        {Array.from({ length: count }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => scrollTo(i)}
            aria-label={`${ariaPrefix} ${i + 1} ${ofLabel} ${count}`}
            aria-current={i === selectedIndex ? "true" : undefined}
            className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              i === selectedIndex ? activeCls : inactiveCls,
            )}
          />
        ))}
      </div>
    );
  }

  // counter + progress bar
  const counterCls = isOverlay
    ? "t-micro tabular-nums tracking-wider text-background/85 [text-shadow:0_1px_3px_rgb(0_0_0_/_0.6)]"
    : "t-micro tabular-nums tracking-wider text-foreground/60";
  const counterActiveCls = isOverlay ? "text-background font-medium" : "text-foreground font-medium";
  const counterSepCls = isOverlay ? "text-background/50" : "text-foreground/30";
  const trackCls = isOverlay ? "bg-background/25" : "bg-foreground/10";
  const fillCls = isOverlay ? "bg-background" : "bg-primary";

  return (
    <div className={cn("flex flex-col items-center gap-2 px-6", className)}>
      <div className={counterCls}>
        <span className={counterActiveCls}>{String(selectedIndex + 1).padStart(2, "0")}</span>
        <span className={cn("mx-1.5", counterSepCls)}>/</span>
        <span>{String(count).padStart(2, "0")}</span>
      </div>
      <div
        className={cn("relative h-[3px] w-24 overflow-hidden rounded-full", trackCls)}
        dir="ltr"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={count}
        aria-valuenow={selectedIndex + 1}
      >
        <div
          className={cn("absolute inset-y-0 left-0 rounded-full transition-[width] duration-150 ease-out", fillCls)}
          style={{ width: `${Math.max(6, effectiveProgress * 100)}%` }}
        />
      </div>
    </div>
  );
}
